/**
 * HolidaysPage — Phase 2 of the Enterprise Workforce Suite.
 *
 * Central place to manage:
 *   * National / State / Festival holidays (org-wide by default).
 *   * Branch-specific closures (renovation, staff outings, …).
 *   * Org-level and emergency closure days.
 *
 * The list is filterable by year, type, and branch. Each row shows the
 * colour swatch that ScheduleView.jsx overlays on the calendar so the
 * super-admin sees exactly what staff will see.
 *
 * Requires the Enterprise Workforce Suite add-on to be active on the
 * workspace (backend enforces 402 otherwise).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Calendar, Plus, PencilSimple, Trash, X, Flag, Sparkle, House, Buildings, Warning,
} from "@phosphor-icons/react";


// Must stay in sync with backend HOLIDAY_TYPES + DEFAULT_TYPE_COLORS.
const HOLIDAY_TYPES = [
  { value: "national", label: "National",      color: "#DC2626", icon: Flag,       hint: "India-wide holidays (Republic Day, Independence Day…)" },
  { value: "state",    label: "State",         color: "#EA580C", icon: Flag,       hint: "State-level (Karnataka Rajyotsava, Onam…)" },
  { value: "festival", label: "Festival",      color: "#D97706", icon: Sparkle,    hint: "Cultural celebrations — may or may not close the org" },
  { value: "branch",   label: "Branch",        color: "#0EA5E9", icon: House,      hint: "Specific branch (renovation, staff outing…)" },
  { value: "org",      label: "Org-wide",      color: "#7C3AED", icon: Buildings,  hint: "Custom org holiday" },
  { value: "closure",  label: "Emergency Closure", color: "#111827", icon: Warning, hint: "Short-notice closure (weather, ops…)" },
];

const typeMeta = (t) => HOLIDAY_TYPES.find(x => x.value === t) || HOLIDAY_TYPES[0];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

const emptyForm = () => ({
  name: "",
  date: new Date().toISOString().slice(0, 10),
  end_date: "",
  type: "national",
  branch_id: "",
  color: "",
  is_working_day: false,
  description: "",
});

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};


export default function HolidaysPage() {
  const { branches } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(currentYear);
  const [typeFilter, setTypeFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // holiday row when editing
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        from_date: `${year}-01-01`,
        to_date:   `${year}-12-31`,
      };
      if (typeFilter !== "all") params.type = typeFilter;
      if (branchFilter !== "all") params.branch_id = branchFilter;
      const { data } = await api.get("/workforce/holidays", { params });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [year, typeFilter, branchFilter]);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const byType = {};
    for (const r of rows) byType[r.type] = (byType[r.type] || 0) + 1;
    return { total: rows.length, byType };
  }, [rows]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "",
      date: row.date || "",
      end_date: row.end_date || "",
      type: row.type || "national",
      branch_id: row.branch_id || "",
      color: row.color || "",
      is_working_day: !!row.is_working_day,
      description: row.description || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.date) { toast.error("Date is required"); return; }
    if (form.type === "branch" && !form.branch_id) {
      toast.error("Branch is required for branch-type holidays");
      return;
    }
    if (form.end_date && form.end_date < form.date) {
      toast.error("End date must be on or after the start date");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        date: form.date,
        end_date: form.end_date || null,
        type: form.type,
        branch_id: form.branch_id || null,
        color: form.color || null,   // backend fills default from type
        is_working_day: !!form.is_working_day,
        description: form.description || "",
      };
      if (editing) {
        await api.put(`/workforce/holidays/${editing.id}`, payload);
        toast.success("Holiday updated");
      } else {
        await api.post("/workforce/holidays", payload);
        toast.success("Holiday added");
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}" on ${row.date}?`)) return;
    try {
      await api.delete(`/workforce/holidays/${row.id}`);
      toast.success("Holiday deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="holidays-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Calendar size={28} className="text-primary" weight="duotone" />
        <div>
          <h1 className="text-2xl font-display font-bold tracking-tight">Holidays</h1>
          <p className="text-xs text-muted-foreground">
            Central calendar of every day the org (or a branch) is closed or highlighted.
            Rendered as coloured bands on the workforce schedule.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
            <SelectTrigger className="h-9 w-28" data-testid="holidays-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-9 w-36" data-testid="holidays-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {HOLIDAY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="holidays-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openAdd} className="gap-1.5" data-testid="holidays-add">
            <Plus size={14} /> Add holiday
          </Button>
        </div>
      </div>

      {/* Compact type-count strip */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="holidays-summary">
        <Badge className="bg-slate-100 text-slate-700 border-slate-200 gap-1">
          {stats.total} total
        </Badge>
        {HOLIDAY_TYPES.map(t => (
          <Badge
            key={t.value}
            className="border-0 gap-1 text-white"
            style={{ background: t.color, opacity: stats.byType[t.value] ? 1 : 0.35 }}
          >
            <t.icon size={11} weight="fill" /> {t.label} {stats.byType[t.value] || 0}
          </Badge>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-display tracking-tight">
            {year} holidays
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No holidays yet for {year}. Click <span className="font-medium">Add holiday</span> to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[240px]">Name</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Working&nbsp;day</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => {
                    const meta = typeMeta(row.type);
                    const branch = (branches || []).find(b => b.id === row.branch_id);
                    return (
                      <TableRow key={row.id} data-testid={`holiday-row-${row.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: row.color || meta.color }} />
                            <div>
                              <div className="text-sm font-medium">{row.name}</div>
                              {row.description && (
                                <div className="text-[10px] text-muted-foreground max-w-[240px] truncate">{row.description}</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs tabular-nums whitespace-nowrap">
                          {fmtDate(row.date)}
                          {row.end_date && row.end_date !== row.date && (
                            <div className="text-[10px] text-muted-foreground">→ {fmtDate(row.end_date)}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className="border-0 text-white gap-1" style={{ background: row.color || meta.color }}>
                            <meta.icon size={11} weight="fill" /> {meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.branch_id
                            ? (branch?.name || "(deleted branch)")
                            : <span className="text-muted-foreground">Org-wide</span>}
                        </TableCell>
                        <TableCell>
                          {row.is_working_day
                            ? <Badge variant="outline" className="text-[10px]">Working</Badge>
                            : <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]">Closed</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="gap-1 h-7 px-2 text-xs" data-testid={`holiday-edit-${row.id}`}>
                            <PencilSimple size={12} /> Edit
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(row)} className="gap-1 h-7 px-2 text-xs text-red-700" data-testid={`holiday-del-${row.id}`}>
                            <Trash size={12} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditing(null); } }}>
        <DialogContent className="sm:max-w-lg bg-card" data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit holiday" : "Add holiday"}</DialogTitle>
            <DialogDescription className="text-xs">
              Fill in the name, date, type and scope. Coloured bands appear on the workforce schedule for every matching day.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Diwali"
                data-testid="holiday-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  data-testid="holiday-date"
                />
              </div>
              <div className="space-y-1.5">
                <Label>End date <span className="text-[10px] text-muted-foreground">(multi-day, optional)</span></Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  min={form.date}
                  data-testid="holiday-end-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="holiday-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HOLIDAY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: t.color }} />
                          {t.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">{typeMeta(form.type).hint}</p>
              </div>

              <div className="space-y-1.5">
                <Label>Branch {form.type === "branch" && <span className="text-red-600">*</span>}</Label>
                <Select
                  value={form.branch_id || "__all__"}
                  onValueChange={v => setForm(f => ({ ...f, branch_id: v === "__all__" ? "" : v }))}
                >
                  <SelectTrigger data-testid="holiday-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Org-wide (all branches)</SelectItem>
                    {(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-md border border-border p-3">
              <Checkbox
                id="holiday-working"
                checked={form.is_working_day}
                onCheckedChange={v => setForm(f => ({ ...f, is_working_day: !!v }))}
                data-testid="holiday-working"
              />
              <Label htmlFor="holiday-working" className="cursor-pointer flex-1">
                <div className="text-sm font-medium">Working day</div>
                <div className="text-[10px] text-muted-foreground">Highlight the day on calendars but staff still report to work.</div>
              </Label>
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional notes"
                data-testid="holiday-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditing(null); }} className="gap-1" data-testid="holiday-cancel">
              <X size={13} /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="holiday-save">
              {saving ? "Saving…" : editing ? "Save changes" : "Add holiday"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
